/**
 * Stock Input History Drawer.
 *
 * Per-product timeline of every StockChangeLog entry — additive inputs,
 * corrections (with reasons), dispatches, bulk uploads. Opened by clicking
 * a part number on the Current Stock or Stock Levels tables. Distinct
 * from the cross-part Change History sub-tab which lists every part for
 * the supplier in one big chronological feed.
 */
import { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import {
  Loader2, Plus, Edit3, Truck, FileSpreadsheet, PackagePlus,
  ArrowRight, Ban, X,
} from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import type { StockChangeLogEntry } from '../../../lib/api';

interface StockInputHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partNumber: string;
  description?: string;
}

interface PartHistoryStats {
  current_total: string | null;
  inputs_30d: string;
  dispatches_30d: string;
  last_input_at: string | null;
}

type ChangeTypeFilter = 'all' | 'inputs' | 'corrections' | 'dispatches';

const PAGE_SIZE = 20;

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return days < 30 ? `${days} d ago` : d.toLocaleDateString();
}

function formatStat(s: string | null | undefined, fallback = '—'): string {
  if (s == null) return fallback;
  const n = parseFloat(s);
  return isNaN(n) ? s : n.toLocaleString();
}

function classifyEntry(entry: StockChangeLogEntry): {
  kind: 'input' | 'correction' | 'dispatch' | 'bulk' | 'cancel' | 'delete' | 'initial';
  label: string;
  chipClass: string;
  cardClass: string;
  Icon: typeof Plus;
  dotClass: string;
} {
  // Corrections are detected via the comments prefix that
  // StockCorrectionService writes — no schema change needed for now.
  const isCorrection = (entry.comments || '').startsWith('Correction by');
  const ct = entry.change_type;

  if (ct === 'dispatch') return {
    kind: 'dispatch', label: 'Dispatch',
    chipClass: 'bg-red-100 text-red-800',
    cardClass: 'border-l-4 border-red-400 bg-red-50',
    Icon: Truck, dotClass: 'bg-red-500',
  };
  if (ct === 'cancellation') return {
    kind: 'cancel', label: 'Cancellation',
    chipClass: 'bg-orange-100 text-orange-800',
    cardClass: 'border-l-4 border-orange-400',
    Icon: Ban, dotClass: 'bg-orange-500',
  };
  if (ct === 'delete') return {
    kind: 'delete', label: 'Deleted',
    chipClass: 'bg-gray-100 text-gray-700',
    cardClass: 'border-l-4 border-gray-300',
    Icon: X, dotClass: 'bg-gray-400',
  };
  if (ct === 'bulk_upload') return {
    kind: 'bulk', label: 'Bulk upload',
    chipClass: 'bg-purple-100 text-purple-800',
    cardClass: 'border-l-4 border-purple-400',
    Icon: FileSpreadsheet, dotClass: 'bg-purple-500',
  };
  if (isCorrection) return {
    kind: 'correction', label: 'Correction',
    chipClass: 'bg-amber-100 text-amber-800',
    cardClass: 'border-l-4 border-amber-400 bg-amber-50',
    Icon: Edit3, dotClass: 'bg-amber-500',
  };
  if (ct === 'insert' || (ct === 'update' && entry.previous_qty == null)) return {
    kind: 'initial', label: 'Initial entry',
    chipClass: 'bg-blue-100 text-blue-800',
    cardClass: 'border-l-4 border-blue-400',
    Icon: PackagePlus, dotClass: 'bg-blue-500',
  };
  return {
    kind: 'input', label: 'Stock input',
    chipClass: 'bg-emerald-100 text-emerald-800',
    cardClass: 'border-l-4 border-emerald-400',
    Icon: Plus, dotClass: 'bg-emerald-500',
  };
}

function entryMatches(entry: StockChangeLogEntry, filter: ChangeTypeFilter): boolean {
  if (filter === 'all') return true;
  const k = classifyEntry(entry).kind;
  if (filter === 'inputs') return k === 'input' || k === 'initial' || k === 'bulk';
  if (filter === 'corrections') return k === 'correction';
  if (filter === 'dispatches') return k === 'dispatch';
  return true;
}

function deltaInfo(entry: StockChangeLogEntry): { delta: number; sign: 'up' | 'down' | 'zero' } {
  const prev = entry.previous_qty == null ? 0 : parseFloat(String(entry.previous_qty));
  const curr = parseFloat(String(entry.current_qty));
  const delta = curr - prev;
  if (delta > 0) return { delta, sign: 'up' };
  if (delta < 0) return { delta, sign: 'down' };
  return { delta: 0, sign: 'zero' };
}

function reasonOf(entry: StockChangeLogEntry): string | null {
  const c = entry.comments || '';
  const match = c.match(/^Correction by [^:]+:\s*(.*)$/);
  return match ? match[1].trim() : null;
}

export default function StockInputHistoryDrawer({
  open, onOpenChange, partNumber, description,
}: StockInputHistoryDrawerProps) {
  const [items, setItems] = useState<StockChangeLogEntry[]>([]);
  const [stats, setStats] = useState<PartHistoryStats | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<ChangeTypeFilter>('all');
  const [loading, setLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!partNumber) return;
    setLoading(true);
    try {
      const res = await callOffAPI.getChangeHistory({
        item_code: partNumber,
        page,
        page_size: PAGE_SIZE,
      });
      setItems(res.data.items || []);
      setTotalCount(res.data.total_count || 0);
      setStats(res.data.stats || null);
    } finally {
      setLoading(false);
    }
  }, [partNumber, page]);

  useEffect(() => {
    if (open) {
      setPage(1);
      setFilter('all');
    }
  }, [open, partNumber]);

  useEffect(() => {
    if (open) fetchHistory();
  }, [open, fetchHistory]);

  const visible = items.filter((e) => entryMatches(e, filter));
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Stock input history</DialogTitle>
          <DialogDescription>
            <span className="font-mono font-medium">{partNumber}</span>
            {description && <span className="text-muted-foreground"> · {description}</span>}
          </DialogDescription>
        </DialogHeader>

        {/* Stat tiles */}
        <div className="grid grid-cols-4 gap-2">
          <StatTile label="Current total" value={formatStat(stats?.current_total)} />
          <StatTile label="Inputs (30d)" value={`+${formatStat(stats?.inputs_30d, '0')}`} positive />
          <StatTile label="Dispatches (30d)" value={formatStat(stats?.dispatches_30d, '0')} negativeIfNonZero />
          <StatTile label="Last input" value={formatRelative(stats?.last_input_at ?? null)} />
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2 pt-2 border-t">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as ChangeTypeFilter)}
            className="h-8 text-xs border border-input rounded-md px-2 bg-background"
            aria-label="Filter change type"
          >
            <option value="all">All change types</option>
            <option value="inputs">Inputs only</option>
            <option value="corrections">Corrections only</option>
            <option value="dispatches">Dispatches only</option>
          </select>
          <span className="text-xs text-muted-foreground ml-auto">
            {totalCount > 0 ? `${visible.length} of ${totalCount} on this page` : 'No entries'}
          </span>
        </div>

        {/* Timeline */}
        <div className="overflow-auto flex-1 -mx-6 px-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              {items.length === 0 ? 'No entries yet for this part.' : 'No entries match the current filter.'}
            </div>
          ) : (
            <div className="relative pl-7">
              <div className="absolute left-[10px] top-0 bottom-0 w-px bg-border" />
              {visible.map((e) => <TimelineRow key={e.id} entry={e} />)}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2 border-t text-xs text-muted-foreground">
            <span>Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1 || loading}
                      onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages || loading}
                      onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatTile({
  label, value, positive, negativeIfNonZero,
}: { label: string; value: string; positive?: boolean; negativeIfNonZero?: boolean }) {
  let toneClass = 'text-foreground';
  if (positive) toneClass = 'text-emerald-700';
  if (negativeIfNonZero && value !== '0' && value !== '—') toneClass = 'text-red-700';
  return (
    <div className="bg-muted/50 rounded-md px-2.5 py-2">
      <p className="text-[0.65rem] uppercase tracking-wide text-muted-foreground font-semibold">{label}</p>
      <p className={`text-base font-bold tabular-nums ${toneClass}`}>{value}</p>
    </div>
  );
}

function TimelineRow({ entry }: { entry: StockChangeLogEntry }) {
  const c = classifyEntry(entry);
  const { delta, sign } = deltaInfo(entry);
  const reason = reasonOf(entry);
  const Icon = c.Icon;

  return (
    <div className="grid grid-cols-[28px_1fr] gap-3 py-3">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center ring-2 ring-background relative z-10 ${c.dotClass}`}>
        <Icon className="w-3.5 h-3.5 text-white" />
      </div>
      <div className={`rounded-md p-3 ${c.cardClass}`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-[0.7rem] font-semibold rounded-full px-2 py-0.5 ${c.chipClass}`}>{c.label}</span>
          <span className="text-xs text-muted-foreground">
            {new Date(entry.created_at).toLocaleString()}
          </span>
          <span className="text-xs text-muted-foreground">·</span>
          <span className="text-xs">{entry.changed_by_name || 'System'}</span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-1.5 bg-background border rounded px-2 py-0.5 tabular-nums">
            <span className="text-muted-foreground">{entry.previous_qty ?? '—'}</span>
            <ArrowRight className="w-3 h-3 text-muted-foreground" />
            <span className="font-semibold">{entry.current_qty}</span>
          </span>
          <span className={
            sign === 'up' ? 'text-emerald-700 font-semibold' :
            sign === 'down' ? 'text-red-700 font-semibold' :
            'text-muted-foreground'
          }>
            {sign === 'up' ? '+' : sign === 'down' ? '' : '±'}{delta}
          </span>
        </div>
        {reason && (
          <p className="mt-2 text-sm leading-snug">
            <span className="text-[0.65rem] uppercase tracking-wide font-semibold text-amber-700">Reason: </span>
            {reason}
          </p>
        )}
        {!reason && entry.comments && c.kind !== 'initial' && (
          <p className="mt-1 text-xs text-muted-foreground">{entry.comments}</p>
        )}
      </div>
    </div>
  );
}
